// A host's approved integrations (§17): which of a document's connected objects reach outside it, by which targets,
// and how a target is compared with a catalog's. One implementation for the editor's completions and checks, the
// renderer's marks and the service's catalog.
import type { Attrs } from "./attributes";
import type { Diagnostic, Node } from "./types";
import type { ReadResult } from "./read";

/** The kinds an integration stands for: MCP servers (an MCP registry's among them), skills and A2A agents. */
export const INTEGRATED_KINDS: ReadonlySet<string> = new Set(["mcp", "skill", "agent"]);

/** A host's catalog: the targets its integrations hold, and whether connected objects are restricted to them. */
export interface Catalog {
  targets: ReadonlySet<string> | readonly string[];
  restricted?: boolean;
}

export type Approval = "approved" | "unapproved";

/** A target as a catalog keeps it: an http(s) URL with its scheme and host lower-cased and its default port, fragment
 *  and trailing slash dropped (its path keeps its case); any other target, a registry's server name, trimmed and
 *  lower-cased. */
export function normalizeTarget(target: string): string {
  const t = target.trim();
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      return `${u.protocol}//${u.host}${u.pathname.length > 1 ? u.pathname.replace(/\/+$/, "") : ""}${u.search}`;
    } catch { /* not a URL after all: compared as written */ }
  }
  return t.toLowerCase();
}

/** The targets by which a connected object reaches outside its document: an http(s) `src` or `endpoint`, a
 *  `registry` or `server` name. Never a relative path (a resource of the document's own bundle) or a `#id`. */
export function remoteTargets(kind: string, attrs: Attrs): string[] {
  if (!INTEGRATED_KINDS.has(kind)) return [];
  const out: string[] = [];
  for (const key of ["src", "endpoint", "registry", "server"]) {
    const v = attrs[key];
    if (typeof v !== "string" || !v.trim() || v.trim().startsWith("#")) continue;
    const url = /^https?:\/\//i.test(v.trim());
    if (url || ((key === "registry" || key === "server") && !/^\.{0,2}\//.test(v.trim()))) out.push(normalizeTarget(v));
  }
  return out;
}

/** Whether a connected object is one of the catalog's: every remote target it names is in it. An object with no remote
 *  target is its document's own (undefined); one outside the catalog is unapproved only under a restriction. */
export function approval(kind: string, attrs: Attrs, catalog: Catalog): Approval | undefined {
  const targets = remoteTargets(kind, attrs);
  if (!targets.length) return undefined;
  const known = catalog.targets instanceof Set ? catalog.targets : new Set(catalog.targets as readonly string[]);
  if (targets.every((t) => known.has(t))) return "approved";
  return catalog.restricted ? "unapproved" : undefined;
}

const NOUN: Record<string, string> = { mcp: "MCP server", skill: "skill", agent: "agent" };

/** The host-policy diagnostics a restriction gives (§17, §18): one warning for each connected object outside the
 *  catalog. Nothing is removed; the object is drawn as not approved and is not resolved. */
export function integrationDiagnostics(r: ReadResult, catalog: Catalog): Diagnostic[] {
  if (!catalog.restricted) return [];
  const out: Diagnostic[] = [];
  const walk = (nodes: Node[]) => {
    for (const n of nodes) {
      if (n.type !== "directive") continue;
      if (approval(n.kind, n.attrs, catalog) === "unapproved") {
        const name = typeof n.attrs.title === "string" ? n.attrs.title : typeof n.attrs.id === "string" ? `#${n.attrs.id}` : n.kind;
        out.push({
          code: "instruction.md/not-integrated", severity: "warning",
          message: `${name}: this ${NOUN[n.kind] ?? n.kind} is not one of your organization's integrations, so it is not resolved or approved`,
          ...(n.range ? { range: { start: n.range.start, end: n.range.start } } : {}),
          remedy: "Use one from your integrations, or ask an admin to add it",
        });
      }
      walk(n.children ?? []);
    }
  };
  walk(r.document.body);
  return out;
}
